import { DesignChannel } from 'components/NewFileModal/Types'
import { StartWithMedia } from '../../components/StartWithMedia/StartWithMedia'
import { PropsWithChildren, useMemo, useState } from 'react'
import { IMediaFile } from 'components/Design/UploadsTab/Types'
import { SetupBasics } from '../../components/SetupBasics/SetupBasics'
import { RefineDetails } from '../../components/RefineDetails/RefineDetails'
import { ChooseTemplate } from '../../components/ChooseTemplate/ChooseTemplate'
import { observer } from 'mobx-react-lite'
import { useMobileWizardCreateDesign } from 'components/Wizards/MobileWizards/hooks/useMobileWizardCreateDesign'
import { MultiStepMobileWizard } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/MultiStepMobileWizard'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { CustomiseLayoutSchema } from './schema'
import { CustomiseLayout } from '../../components/CustomiseLayout/CustomiseLayout'
import { SubmitStepProps } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/Types'
import { DesignValues, FlowProps } from 'components/Wizards/MobileWizards/Types'
import { useVisualElement } from 'hooks/queries/visualElements/useVisualElement'
import { VisualElementsClient } from 'api/clients/VisualElementClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { reportError } from 'utils/error'

type Values = Pick<
  DesignValues,
  | 'initial_media_file_id'
  | 'initial_media_file_remove_background'
  | 'width'
  | 'height'
  | 'filename'
  | 'project'
  | 'source_visual_element_id'
  | 'topic'
  | 'brand_voice_id'
  | 'tone'
  | 'language'
  | 'brand_kit_id'
  | 'content'
  | 'palette'
  | 'text_styles'
>

const FlowA1WithInitialTemplateWithReplaceableImage = observer(
  ({ onClose, initialValues, ...props }: FlowProps<DesignChannel>) => {
    const schema = useMemo(
      () => ({
        ...props.schema,
        steps: [...props.schema.steps.slice(0, 3)],
      }),
      [props.schema],
    )

    const [selectedMedia, setSelectedMedia] = useState<IMediaFile | undefined>()

    return (
      <FlowA1 initialValues={initialValues} {...props} schema={schema} onClose={onClose}>
        <StartWithMedia onSelect={setSelectedMedia} hideAltWizard />
        {selectedMedia && <SetupBasics selectedMedia={selectedMedia} hideAspectRatio />}
        <RefineDetails submitLabel="Generate Post" />
      </FlowA1>
    )
  },
)

const FlowA1WithInitialTemplateWithoutReplaceableImage = observer(
  ({ onClose, initialValues, ...props }: FlowProps<DesignChannel>) => {
    const schema = useMemo(
      () => ({
        ...props.schema,
        steps: [props.schema.steps[2]],
      }),
      [props.schema],
    )

    return (
      <FlowA1 initialValues={initialValues} onClose={onClose} {...props} schema={schema}>
        <RefineDetails submitLabel="Generate Post" />
      </FlowA1>
    )
  },
)

const FlowA1WithoutInitialTemplate = observer(
  ({ onClose, initialValues, schema, ...props }: FlowProps<DesignChannel>) => {
    const layoutCustomisationEnabled = useFlipperFlag('mobile_redesign_wizard_A1_layout_customisation')
    const schemaOverride = useMemo(() => {
      if (layoutCustomisationEnabled) {
        return {
          ...schema,
          steps: [...schema.steps, CustomiseLayoutSchema],
        }
      }

      return schema
    }, [layoutCustomisationEnabled, schema])

    const [selectedMedia, setSelectedMedia] = useState<IMediaFile | undefined>()

    return (
      <FlowA1 initialValues={initialValues} schema={schemaOverride} onClose={onClose} {...props}>
        <StartWithMedia onSelect={setSelectedMedia} />
        {selectedMedia && <SetupBasics selectedMedia={selectedMedia} />}
        <RefineDetails />
        <ChooseTemplate />
        {layoutCustomisationEnabled && <CustomiseLayout />}
      </FlowA1>
    )
  },
)

const FlowA1 = observer(({ children, schema, ...props }: PropsWithChildren<FlowProps<DesignChannel>>) => {
  const layoutCustomisationEnabled = useFlipperFlag('mobile_redesign_wizard_A1_layout_customisation')

  const {
    meta: { channel },
  } = schema

  const { createDesign } = useMobileWizardCreateDesign(channel, {
    navigateToDesignOnComplete: true,
    loadingToast: 'Generating your post...',
  })

  const handleSubmitStep = async ({ currentStep, totalSteps, values, advance, schema }: SubmitStepProps<Values>) => {
    const { stepId } = schema.describe().meta

    if (layoutCustomisationEnabled && stepId === 'ChooseTemplate') {
      const { palette, text_styles: textStyles, source_visual_element_id: templateId } = values

      if (palette && palette.length === 0 && textStyles && textStyles.length === 0) {
        await createDesign(values)
        return
      }

      if (templateId) {
        const res = await VisualElementsClient.getElement(templateId)
        if (res.type !== SUCCESS_RESPONSE_TYPE || !res.data.basic_layout) {
          if (res.type !== SUCCESS_RESPONSE_TYPE) {
            reportError(new Error('Failed to fetch template'), { metaData: { templateId } })
          }
          await createDesign(values)
          return
        }
      }
    }

    const generationStepIndex = totalSteps - 1

    if (currentStep === generationStepIndex) {
      await createDesign(values)
      return
    }

    advance()
  }

  return (
    <MultiStepMobileWizard<Values> schema={schema} onSubmitStep={handleSubmitStep} {...props} renderHeader={true}>
      {children}
    </MultiStepMobileWizard>
  )
})

const FlowA1WithTemplateContainer = (
  props: FlowProps<DesignChannel> & { initialValues: Partial<DesignValues> & { source_visual_element_id: number } },
) => {
  const { data: template } = useVisualElement({ id: props.initialValues.source_visual_element_id })

  if (template) {
    if (template.has_replaceable) {
      return <FlowA1WithInitialTemplateWithReplaceableImage {...props} />
    }
    return <FlowA1WithInitialTemplateWithoutReplaceableImage {...props} />
  }

  return <FlowA1WithoutInitialTemplate {...props} />
}

const FlowA1Container = ({ initialValues, ...props }: FlowProps<DesignChannel>) => {
  const { source_visual_element_id, width, height } = initialValues || {}

  if (source_visual_element_id && width && height) {
    return (
      <FlowA1WithTemplateContainer
        initialValues={{ ...initialValues, source_visual_element_id, width, height }}
        {...props}
      />
    )
  }

  return <FlowA1WithoutInitialTemplate initialValues={initialValues} {...props} />
}

export { FlowA1Container as FlowA1 }
